/**
 * Pantalla encendida durante el entrenamiento (Req. 11.12; design.md, 9).
 *
 * Usa la API Screen Wake Lock (en la app instalada del iPhone, iOS 18.4 o
 * posterior). Desde la 0.3.8 se muestra el estado REAL del candado, no la
 * casilla (incidencia del 26 oct 2026):
 *
 * - Safari (WebKit) solo lo concede en respuesta a un toque (activación
 *   transitoria; la especificación lo pide desde 2025). Pedirlo al abrir la
 *   pantalla o al volver de segundo plano se rechaza con NotAllowedError, y
 *   antes ese rechazo no se veía. Ahora se pide al tocar la casilla y, si hace
 *   falta, con el siguiente toque en la pantalla.
 * - El sistema lo suelta al ir a segundo plano o al bloquear el iPhone, y
 *   puede soltarlo o negarlo por su cuenta (por ejemplo, con poca batería):
 *   la pantalla lo dice y se vuelve a pedir con un toque.
 * - Cada paso se anota en este teléfono (los últimos 20) para revisarlo en el
 *   iPhone; no se atribuye a una causa que el navegador no dio.
 * - 0.3.9: si el navegador dice si hubo un toque (`navigator.userActivation`),
 *   eso decide entre "falta un toque" y "la negó"; un toque que no cuenta no
 *   la pide, tras negarla con un toque no se insiste con cada toque, y lo que
 *   se repite se cuenta en una sola línea del registro.
 */

import { useEffect, useSyncExternalStore } from 'react';

export type EstadoPantalla =
  | { tipo: 'sin_soporte' }
  /** La preferencia está apagada o no hay entrenamiento a la vista. */
  | { tipo: 'apagada' }
  | { tipo: 'pidiendo' }
  | { tipo: 'activa' }
  /** El navegador la negó sin un toque: se pide otra vez con el siguiente. */
  | { tipo: 'falta_toque' }
  /** La tenía y el sistema la soltó. */
  | { tipo: 'liberada' }
  /** La negó aun con un toque, con el motivo que dio el navegador. */
  | { tipo: 'rechazada'; motivo: string };

export interface Candado {
  release: () => Promise<void>;
  addEventListener: (tipo: 'release', fn: () => void) => void;
}

export interface ApiCandado {
  request: (tipo: 'screen') => Promise<Candado>;
}

export interface EntornoPantalla {
  api: ApiCandado | null;
  visible: () => boolean;
  /** ¿Hay un toque reciente (activación transitoria)? null si el navegador no lo dice. */
  conToque: () => boolean | null;
  ahora: () => number;
}

export interface EventoPantalla {
  /** Instante ISO (la última vez, si se repitió). */
  en: string;
  texto: string;
  /** Cuántas veces seguidas pasó lo mismo (0.3.9); sin el campo, una. */
  veces?: number;
  /** Instante ISO de la primera de esas veces. */
  desde?: string;
}

const MAXIMO_REGISTRO = 20;

/**
 * Después de que la niega aun con un toque, los toques siguientes no la piden
 * otra vez antes de este tiempo (0.3.9): antes cada toque la volvía a pedir y
 * el registro se llenaba con lo mismo.
 */
export const ESPERA_TRAS_NEGAR_MS = 15_000;

/** Qué la pidió. */
type Origen = 'abrir' | 'casilla' | 'toque' | 'volver';

const TEXTO_ORIGEN: Record<Origen, string> = {
  abrir: 'al abrir el entrenamiento',
  casilla: 'al tocar la casilla',
  toque: 'al tocar la pantalla',
  volver: 'al volver a la app',
};

/**
 * El candado de la pantalla y su estado. Sin React, para probarlo con un
 * navegador simulado. `quiere` = la pantalla del entrenamiento está abierta
 * con la preferencia activa.
 */
export class ControlPantalla {
  private quiere = false;
  private candado: Candado | null = null;
  private pidiendo = false;
  /** Cuándo la negó aun con un toque, para no insistir con cada toque. */
  private negadaConToqueEn: number | null = null;
  private actual: EstadoPantalla;
  private readonly oyentes = new Set<() => void>();
  readonly registro: EventoPantalla[];

  constructor(
    private readonly entorno: EntornoPantalla,
    registroInicial: readonly EventoPantalla[] = [],
    private readonly guardarRegistro: (r: readonly EventoPantalla[]) => void = () => {},
  ) {
    this.actual = entorno.api === null ? { tipo: 'sin_soporte' } : { tipo: 'apagada' };
    this.registro = registroInicial.slice(-MAXIMO_REGISTRO);
  }

  get estado(): EstadoPantalla {
    return this.actual;
  }

  readonly suscribir = (fn: () => void): (() => void) => {
    this.oyentes.add(fn);
    return () => {
      this.oyentes.delete(fn);
    };
  };

  readonly leer = (): EstadoPantalla => this.actual;

  private fijar(estado: EstadoPantalla): void {
    this.actual = estado;
    for (const fn of this.oyentes) fn();
  }

  /** Anota un paso; si es igual al anterior, lo cuenta en lugar de repetirlo (0.3.9). */
  private anotar(texto: string): void {
    const en = new Date(this.entorno.ahora()).toISOString();
    const ultimo = this.registro[this.registro.length - 1];
    if (ultimo !== undefined && ultimo.texto === texto) {
      this.registro[this.registro.length - 1] = { en, texto, veces: (ultimo.veces ?? 1) + 1, desde: ultimo.desde ?? ultimo.en };
    } else {
      this.registro.push({ en, texto });
      if (this.registro.length > MAXIMO_REGISTRO) this.registro.splice(0, this.registro.length - MAXIMO_REGISTRO);
    }
    this.guardarRegistro(this.registro);
  }

  /**
   * Pide el candado si hace falta. Si ya hay un pedido en camino (por ejemplo,
   * al volver a entrar mientras el de antes no termina), lo espera mostrando
   * "Activando…" en lugar de quedar sin estado (0.3.9).
   */
  private solicitar(origen: Origen): void {
    if (!this.quiere || this.candado !== null) return;
    if (this.pidiendo) {
      if (this.actual.tipo === 'apagada') this.fijar({ tipo: 'pidiendo' });
      return;
    }
    void this.pedir(origen);
  }

  /** La pantalla del entrenamiento se abrió con la preferencia activa. */
  activar(): void {
    if (this.entorno.api === null) return;
    this.quiere = true;
    this.solicitar('abrir');
  }

  /** Dentro del toque que activa la preferencia: así Safari la concede. */
  pedirConToque(): void {
    if (this.entorno.api === null) return;
    this.quiere = true;
    this.solicitar('casilla');
  }

  /**
   * Cualquier toque en la pantalla: si hace falta, se pide ahora que cuenta
   * como toque. No se pide si el navegador dice que ese toque no cuenta (por
   * ejemplo, al desplazarse) ni, si ya la negó aun con un toque, antes de
   * `ESPERA_TRAS_NEGAR_MS` (0.3.9).
   */
  toque(): void {
    if (!this.quiere || this.candado !== null || this.pidiendo) return;
    if (this.entorno.conToque() === false) return;
    if (this.negadaConToqueEn !== null && this.entorno.ahora() - this.negadaConToqueEn < ESPERA_TRAS_NEGAR_MS) return;
    this.solicitar('toque');
  }

  /** La app volvió a verse. */
  alVolver(): void {
    this.solicitar('volver');
  }

  /** Se cerró la pantalla del entrenamiento o se apagó la preferencia. */
  desactivar(): void {
    if (this.entorno.api === null) return;
    const teniaAlgo = this.quiere;
    this.quiere = false;
    this.negadaConToqueEn = null;
    const candado = this.candado;
    this.candado = null;
    if (candado !== null) {
      void candado.release().catch(() => {});
      this.anotar('la app la soltó (salió del entrenamiento o se apagó la opción)');
    }
    if (teniaAlgo || this.actual.tipo !== 'apagada') this.fijar({ tipo: 'apagada' });
  }

  private async pedir(origen: Origen): Promise<void> {
    const api = this.entorno.api;
    if (api === null || !this.quiere) return;
    // Oculta no se puede pedir: se pide al volver
    if (!this.entorno.visible()) return;
    // Lo que dice el navegador del toque, si lo dice (0.3.9); si no, se supone por el origen
    const informado = this.entorno.conToque();
    const esToque = origen === 'toque' || origen === 'casilla';
    const conToque = informado ?? esToque;
    this.pidiendo = true;
    if (this.actual.tipo !== 'activa') this.fijar({ tipo: 'pidiendo' });
    let candado: Candado;
    try {
      candado = await api.request('screen');
    } catch (e) {
      this.pidiendo = false;
      const nombre = e instanceof Error ? e.name : 'Error';
      const mensaje = e instanceof Error && e.message !== '' ? `: ${e.message}` : '';
      // Sin repetir el origen: "al tocar la pantalla (el navegador no lo contó como toque)", "al volver a la app (sin un toque)"
      const delToque = esToque ? (conToque ? '' : ' (el navegador no lo contó como toque)') : conToque ? ' (con un toque reciente)' : ' (sin un toque)';
      this.anotar(`la negó ${TEXTO_ORIGEN[origen]}${delToque}: ${nombre}${mensaje}`);
      if (conToque) this.negadaConToqueEn = this.entorno.ahora();
      if (!this.quiere) return;
      this.fijar(nombre === 'NotAllowedError' && !conToque ? { tipo: 'falta_toque' } : { tipo: 'rechazada', motivo: `${nombre}${mensaje}` });
      return;
    }
    this.pidiendo = false;
    if (!this.quiere) {
      void candado.release().catch(() => {});
      return;
    }
    this.candado = candado;
    this.negadaConToqueEn = null;
    this.anotar(`activa (${TEXTO_ORIGEN[origen]})`);
    this.fijar({ tipo: 'activa' });
    candado.addEventListener('release', () => {
      // Si la soltó la app, ya no es la de ahora
      if (this.candado !== candado) return;
      this.candado = null;
      this.anotar(this.entorno.visible() ? 'el sistema la soltó con la app a la vista' : 'el sistema la soltó al dejar de verse la app');
      if (this.quiere) this.fijar({ tipo: 'liberada' });
    });
  }
}

// ---------------------------------------------------------------------------
// En la app
// ---------------------------------------------------------------------------

const CLAVE_REGISTRO = 'duofit-pantalla-registro';

function api(): ApiCandado | null {
  if (typeof navigator === 'undefined') return null;
  const n = navigator as unknown as { wakeLock?: ApiCandado };
  return n.wakeLock ?? null;
}

export const PANTALLA_ENCENDIDA_SOPORTADA = api() !== null;

function leerRegistro(): EventoPantalla[] {
  try {
    const crudo = typeof localStorage === 'undefined' ? null : localStorage.getItem(CLAVE_REGISTRO);
    const datos: unknown = crudo === null ? [] : JSON.parse(crudo);
    if (!Array.isArray(datos)) return [];
    return datos.flatMap((e: Partial<Record<keyof EventoPantalla, unknown>> | null): EventoPantalla[] => {
      if (typeof e?.en !== 'string' || typeof e.texto !== 'string') return [];
      const veces = typeof e.veces === 'number' && Number.isInteger(e.veces) && e.veces > 1 ? e.veces : null;
      return [veces === null ? { en: e.en, texto: e.texto } : { en: e.en, texto: e.texto, veces, desde: typeof e.desde === 'string' ? e.desde : e.en }];
    });
  } catch {
    return [];
  }
}

let compartido: ControlPantalla | null = null;

/** El control de este teléfono (uno por página). */
export function controlPantalla(): ControlPantalla {
  if (compartido === null) {
    compartido = new ControlPantalla(
      {
        api: api(),
        visible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
        conToque: () => {
          const ua = (navigator as unknown as { userActivation?: { isActive: boolean } }).userActivation;
          return ua === undefined ? null : ua.isActive;
        },
        ahora: () => Date.now(),
      },
      leerRegistro(),
      (r) => {
        try {
          localStorage.setItem(CLAVE_REGISTRO, JSON.stringify(r));
        } catch {
          // Sin espacio: el registro es solo para revisar, no se pierde nada más
        }
      },
    );
  }
  return compartido;
}

/**
 * Mientras la pantalla del entrenamiento está abierta y la preferencia
 * activa, pide el candado y lo vuelve a pedir al volver o con un toque.
 * Devuelve el estado real para mostrarlo.
 */
export function usePantallaEncendida(preferencia: boolean): EstadoPantalla {
  const control = controlPantalla();
  const estado = useSyncExternalStore(control.suscribir, control.leer, control.leer);

  useEffect(() => {
    if (!preferencia) {
      control.desactivar();
      return;
    }
    control.activar();
    const alToque = () => control.toque();
    const alVolver = () => {
      if (document.visibilityState === 'visible') control.alVolver();
    };
    // touchend y click cuentan como toque para Safari; pointerdown, no
    document.addEventListener('touchend', alToque, { capture: true, passive: true });
    document.addEventListener('click', alToque, true);
    document.addEventListener('keydown', alToque, true);
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('pageshow', alVolver);
    return () => {
      document.removeEventListener('touchend', alToque, { capture: true });
      document.removeEventListener('click', alToque, true);
      document.removeEventListener('keydown', alToque, true);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('pageshow', alVolver);
      control.desactivar();
    };
  }, [preferencia, control]);

  return estado;
}
